/**
 * SystemHealthPanel - Platform health status display
 *
 * Features:
 * - Overall status with pulsing indicator
 * - Grid of service status badges
 * - Color-coded: emerald=healthy, amber=degraded, red=down
 * - Link to Operations page for details
 * - Gradient background matching status
 */
import { Card, CardContent } from '@respectly/ui';
import { Button } from '@respectly/ui';
import { cn } from '@respectly/ui';
import {
  Activity,
  ArrowUpRight,
  Database,
  Server,
  Zap,
  Mail,
  Phone,
  CreditCard,
  Mic,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { useNavigate } from '@tanstack/react-router';
import type { PlatformHealthStatus } from '@respectly/shared-types';

// ============================================
// Types
// ============================================

interface SystemHealthPanelProps {
  healthStatus: PlatformHealthStatus | null;
  isLoading: boolean;
}

// Service icon mapping
const SERVICE_ICONS: Record<string, React.ElementType> = {
  api: Server,
  database: Database,
  redis: Zap,
  sinch: Phone,
  mailgun: Mail,
  ultravox: Mic,
  stripe: CreditCard,
};

// ============================================
// Component
// ============================================

export function SystemHealthPanel({ healthStatus, isLoading }: SystemHealthPanelProps) {
  const navigate = useNavigate();

  if (isLoading) {
    return <SystemHealthPanelSkeleton />;
  }

  const overallStatus = healthStatus?.overallStatus || 'operational';
  const services = healthStatus?.services || [];

  const statusConfig = {
    operational: {
      color: 'emerald',
      bg: 'from-emerald-50/50 to-transparent dark:from-emerald-950/20',
      border: 'emerald-500/20',
      text: 'All Systems Operational',
      textColor: 'text-emerald-800 dark:text-emerald-300',
      subText: 'text-emerald-600 dark:text-emerald-400/80',
      iconBg: 'bg-emerald-500/20',
      iconColor: 'text-emerald-600 dark:text-emerald-400',
      pulseColor: 'bg-emerald-400',
      dotColor: 'bg-emerald-500',
    },
    degraded: {
      color: 'amber',
      bg: 'from-amber-50/50 to-transparent dark:from-amber-950/20',
      border: 'amber-500/20',
      text: 'Some Services Degraded',
      textColor: 'text-amber-800 dark:text-amber-300',
      subText: 'text-amber-600 dark:text-amber-400/80',
      iconBg: 'bg-amber-500/20',
      iconColor: 'text-amber-600 dark:text-amber-400',
      pulseColor: 'bg-amber-400',
      dotColor: 'bg-amber-500',
    },
    down: {
      color: 'red',
      bg: 'from-red-50/50 to-transparent dark:from-red-950/20',
      border: 'red-500/20',
      text: 'System Issues Detected',
      textColor: 'text-red-800 dark:text-red-300',
      subText: 'text-red-600 dark:text-red-400/80',
      iconBg: 'bg-red-500/20',
      iconColor: 'text-red-600 dark:text-red-400',
      pulseColor: 'bg-red-400',
      dotColor: 'bg-red-500',
    },
  };

  const config = statusConfig[overallStatus as keyof typeof statusConfig] || statusConfig.operational;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.05 }}
    >
      <Card
        className={cn(
          'border-slate-200/60 dark:border-slate-700/60',
          `bg-gradient-to-br ${config.bg}`
        )}
      >
        <CardContent className="p-5">
          <div className="flex items-start justify-between gap-4">
            {/* Status Indicator & Text */}
            <div className="flex items-center gap-4 flex-1 min-w-0">
              <div className="relative flex-shrink-0">
                <div className={cn('rounded-xl p-3', config.iconBg)}>
                  <Activity className={cn('h-6 w-6', config.iconColor)} />
                </div>
                {/* Pulsing indicator */}
                <span className="absolute -top-0.5 -right-0.5 flex h-3 w-3">
                  <span
                    className={cn(
                      'animate-ping absolute inline-flex h-full w-full rounded-full opacity-75',
                      config.pulseColor
                    )}
                  />
                  <span
                    className={cn(
                      'relative inline-flex rounded-full h-3 w-3',
                      config.dotColor
                    )}
                  />
                </span>
              </div>

              <div className="flex-1 min-w-0">
                <p className={cn('font-semibold', config.textColor)}>{config.text}</p>
                <p className={cn('text-sm mt-0.5', config.subText)}>
                  Last checked: Just now
                </p>
              </div>
            </div>

            {/* Service Status Badges */}
            {services.length > 0 && (
              <div className="hidden sm:flex items-center gap-2 flex-wrap">
                {services.slice(0, 7).map((service) => {
                  const ServiceIcon = SERVICE_ICONS[service.service.toLowerCase()] || Server;
                  const isHealthy = service.status === 'healthy';
                  const isDegraded = service.status === 'degraded';

                  return (
                    <div
                      key={service.service}
                      className={cn(
                        'flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium',
                        isHealthy &&
                          'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400',
                        isDegraded &&
                          'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400',
                        !isHealthy &&
                          !isDegraded &&
                          'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                      )}
                      title={service.service}
                    >
                      <ServiceIcon className="h-3.5 w-3.5" />
                      <span className="capitalize">{service.service}</span>
                    </div>
                  );
                })}
              </div>
            )}

            {/* View Details Button */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate({ to: '/operations' })}
              className={cn(
                'text-sm font-medium hover:underline flex items-center gap-1 flex-shrink-0',
                config.subText
              )}
            >
              View Details
              <ArrowUpRight className="h-3.5 w-3.5" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}

// ============================================
// Skeleton
// ============================================

export function SystemHealthPanelSkeleton() {
  return (
    <Card className="border-slate-200/60 dark:border-slate-700/60">
      <CardContent className="p-5">
        <div className="flex items-center gap-4">
          <div className="animate-pulse flex items-center gap-4 flex-1">
            <div className="h-14 w-14 bg-slate-200 dark:bg-slate-700 rounded-xl" />
            <div className="flex-1 space-y-2">
              <div className="h-5 w-48 bg-slate-200 dark:bg-slate-700 rounded" />
              <div className="h-4 w-32 bg-slate-200 dark:bg-slate-700 rounded" />
            </div>
          </div>
          <div className="h-8 w-28 bg-slate-200 dark:bg-slate-700 rounded animate-pulse" />
        </div>
      </CardContent>
    </Card>
  );
}
